"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Badge, Button, Label, cn, confirm, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  GaugeIcon,
  PhoneIcon,
  SaveIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { WorkshopJobStatusDTO } from "../../domain/contracts";
import {
  useSetWorkshopJobStatusMutation,
  useUpdateWorkshopJobMutation,
  useWorkshopJobQuery,
} from "../api/queries";
import {
  WORKSHOP_STATUS_LABELS,
  formatWorkshopDateTime,
  pickWorkshopPrimaryNext,
  statusToneClass,
} from "../lib/workshop-labels";
import { workshopToastError } from "../lib/workshop-user-messages";
import { WorkshopJobLaborsSummary } from "../ui/WorkshopJobLaborsSummary";
import { WorkshopJobPartsSummary } from "../ui/WorkshopJobPartsSummary";
import { WorkshopLaborDialog } from "../ui/WorkshopLaborDialog";
import { WorkshopPartsDialog } from "../ui/WorkshopPartsDialog";
import {
  WorkshopStatusPrimaryButton,
  WorkshopStatusSecondaryButton,
} from "../ui/WorkshopStatusActionButton";
import { WorkshopQuoteCheckoutPanel } from "../ui/WorkshopQuoteCheckoutPanel";
import { WorkshopStatusStepper } from "../ui/WorkshopStatusStepper";

const fieldClass =
  "border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[72px] w-full rounded-xl border px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

function phoneHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : "#";
}

function Section({
  title,
  count,
  children,
  className,
}: {
  title: string;
  count?: number;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-2xl border border-border bg-card p-4 shadow-sm",
        className,
      )}
    >
      <h2 className="mb-3 flex items-baseline gap-2 font-semibold text-base">
        <span>{title}</span>
        {count != null ? (
          <span className="font-normal text-muted-foreground text-sm">
            {count} ລາຍການ
          </span>
        ) : null}
      </h2>
      {children}
    </section>
  );
}

export function ShopWorkshopJobDetailPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const jobId = params?.jobId as string | undefined;
  const nav = useNavigate();
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.workshop.create,
    currentShop?.verifiedLevel ?? null,
  );

  const jobQ = useWorkshopJobQuery(shopId, jobId);
  const statusMut = useSetWorkshopJobStatusMutation(shopId);
  const updateMut = useUpdateWorkshopJobMutation(shopId);

  const job = jobQ.data;
  const [complaint, setComplaint] = useState("");
  const [workNotes, setWorkNotes] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [partsDialogOpen, setPartsDialogOpen] = useState(false);
  const [laborDialogOpen, setLaborDialogOpen] = useState(false);

  useEffect(() => {
    if (job) {
      setComplaint(job.complaint ?? "");
      setWorkNotes(job.workNotes ?? "");
      if (job.complaint || job.workNotes) setNotesOpen(true);
    }
  }, [job]);

  const primaryNext = useMemo(
    () => pickWorkshopPrimaryNext(job?.allowedNextStatuses ?? []),
    [job?.allowedNextStatuses],
  );

  const secondaryNext = useMemo(() => {
    if (!job?.allowedNextStatuses?.length) return [];
    return job.allowedNextStatuses.filter((s) => s !== primaryNext);
  }, [job?.allowedNextStatuses, primaryNext]);

  async function changeStatus(status: WorkshopJobStatusDTO) {
    if (!jobId) return;
    try {
      await statusMut.mutateAsync({ jobId, body: { status } });
      toast.success(`ປ່ຽນເປັນ ${WORKSHOP_STATUS_LABELS[status]}`);
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  function handleStatusClick(status: WorkshopJobStatusDTO) {
    void (async () => {
      if (!job) return;
      const fromLabel = WORKSHOP_STATUS_LABELS[job.status];
      const toLabel = WORKSHOP_STATUS_LABELS[status];
      const isCancel = status === "cancelled";

      const ok = await confirm({
        title: isCancel ? "ຍົກເລີກໃບງານ" : "ປ່ຽນສະຖານະ",
        description: isCancel
          ? `ຢືນຢັນຍົກເລີກ ${job.jobNumber} · ${job.plateNumber} ຫຼື ບໍ່?`
          : `ປ່ຽນຈາກ «${fromLabel}» ເປັນ «${toLabel}» ຫຼື ບໍ່?`,
        actionText: isCancel ? "ຍົກເລີກ" : "ຢືນຢັນ",
        ActionProps: isCancel ? { variant: "destructive" } : undefined,
      });
      if (!ok) return;
      await changeStatus(status);
    })();
  }

  function openPartsEditor() {
    setPartsDialogOpen(true);
  }

  function openAddLaborDialog() {
    setLaborDialogOpen(true);
  }

  async function saveNotes() {
    if (!jobId) return;
    try {
      await updateMut.mutateAsync({
        jobId,
        body: {
          complaint: complaint.trim(),
          workNotes: workNotes.trim() || null,
        },
      });
      toast.success("ບັນທຶກແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  const locked = job?.status === "paid" || job?.status === "cancelled";
  const canEdit = Boolean(canCreate && !locked);
  const showStickyCta = Boolean(canEdit && primaryNext && job);

  return (
    <RequireMerchantPermission permission={MerchantPermissions.workshop.read}>
      <Header>
        <Breadcrumb
          items={shopBreadcrumbs.workshopJobDetail(shopId, job?.jobNumber)}
        />
      </Header>
      <Main
        className={cn(
          showStickyCta
            ? "pb-[calc(7.5rem+env(safe-area-inset-bottom))] md:pb-8"
            : "pb-8",
        )}
      >
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 py-2 md:py-4">
          {jobQ.isLoading ? (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
              <div className="h-48 animate-pulse rounded-2xl bg-muted" />
              <div className="h-64 animate-pulse rounded-2xl bg-muted" />
            </div>
          ) : jobQ.isError || !job ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-10 text-center">
              <p className="font-medium text-destructive text-sm">
                ບໍ່ພົບໃບງານ
              </p>
              {shopId ? (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3"
                  onClick={() =>
                    void nav({
                      to: "/app/shops/$shopId/workshop",
                      params: { shopId },
                    })
                  }
                >
                  ກັບກະດານ
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
              {/* Left column — vehicle + status (sticky on desktop) */}
              <div className="flex flex-col gap-4 lg:sticky lg:top-4">
                <section className="overflow-hidden rounded-2xl border border-border bg-linear-to-br from-muted/70 via-background to-background shadow-sm">
                  {shopId ? (
                    <div className="border-border/60 border-b px-4 py-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-muted-foreground text-sm touch-manipulation transition-colors hover:text-foreground"
                        onClick={() =>
                          void nav({
                            to: "/app/shops/$shopId/workshop",
                            params: { shopId },
                          })
                        }
                      >
                        <ArrowLeftIcon className="size-4" />
                        ກັບກະດານ
                      </button>
                    </div>
                  ) : null}
                  <div className="flex items-start justify-between gap-3 border-border/60 border-b px-4 pt-4 pb-3">
                    <div className="min-w-0">
                      <p className="font-mono text-muted-foreground text-xs tracking-wide">
                        {job.jobNumber}
                      </p>
                      <h1 className="mt-0.5 font-bold text-3xl tracking-tight md:text-4xl">
                        {job.plateNumber}
                      </h1>
                      <p className="mt-1 text-muted-foreground text-xs">
                        ຮັບລົດ {formatWorkshopDateTime(job.createdAt)}
                      </p>
                    </div>
                    <Badge
                      className={cn(
                        "shrink-0 border px-2.5 py-1 text-xs",
                        statusToneClass(job.status),
                      )}
                      variant="secondary"
                    >
                      {WORKSHOP_STATUS_LABELS[job.status]}
                    </Badge>
                  </div>

                  {(job.odometer != null ||
                    job.vehicleVin ||
                    job.vehicleFrameNumber ||
                    job.vehicleEngineCode) && (
                      <div className="flex flex-wrap gap-2 px-4 pt-3">
                        {job.odometer != null ? (
                          <span className="inline-flex items-center gap-1 rounded-lg border border-border bg-background/80 px-2.5 py-1 text-xs">
                            <GaugeIcon className="size-3.5 text-muted-foreground" />
                            {job.odometer.toLocaleString()} km
                          </span>
                        ) : null}
                        {job.vehicleEngineCode ? (
                          <span className="rounded-lg border border-border bg-background/80 px-2.5 py-1 font-mono text-xs uppercase">
                            {job.vehicleEngineCode}
                          </span>
                        ) : null}
                        {job.vehicleFrameNumber ? (
                          <span className="rounded-lg border border-border bg-background/80 px-2.5 py-1 font-mono text-xs">
                            Frame …{job.vehicleFrameNumber.slice(-10)}
                          </span>
                        ) : null}
                        {job.vehicleVin ? (
                          <span className="rounded-lg border border-border bg-background/80 px-2.5 py-1 font-mono text-xs">
                            VIN …{job.vehicleVin.slice(-8)}
                          </span>
                        ) : null}
                      </div>
                    )}

                  <div className="flex items-center justify-between gap-3 px-4 py-3.5">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-sm">
                        {job.customerName}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {job.customerPhone}
                      </p>
                    </div>
                    <a
                      href={phoneHref(job.customerPhone)}
                      className="inline-flex h-12 shrink-0 items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 font-semibold text-emerald-900 text-sm touch-manipulation transition hover:bg-emerald-100 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-100 dark:hover:bg-emerald-500/20"
                    >
                      <PhoneIcon className="size-4" />
                      ໂທຫາ
                    </a>
                  </div>
                </section>

                <Section title="ສະຖານະວຽກ">
                  <WorkshopStatusStepper status={job.status} />

                  {canEdit && primaryNext ? (
                    <WorkshopStatusPrimaryButton
                      to={primaryNext}
                      from={job.status}
                      pending={statusMut.isPending}
                      className="mt-3 hidden md:inline-flex"
                      onClick={() => handleStatusClick(primaryNext)}
                    />
                  ) : null}

                  {canEdit && secondaryNext.length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {secondaryNext.map((s) => (
                        <WorkshopStatusSecondaryButton
                          key={s}
                          status={s}
                          pending={statusMut.isPending}
                          onClick={() => handleStatusClick(s)}
                        />
                      ))}
                    </div>
                  ) : null}
                </Section>

                {/* Notes — desktop left column */}
                <section className="hidden rounded-2xl border border-border bg-card p-4 shadow-sm lg:block">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left touch-manipulation"
                    onClick={() => setNotesOpen((o) => !o)}
                  >
                    <div className="min-w-0">
                      <h2 className="font-semibold text-base">
                        ອາການ / ໝາຍເຫດ
                      </h2>
                      {!notesOpen && job.complaint ? (
                        <p className="mt-0.5 line-clamp-2 text-muted-foreground text-xs">
                          {job.complaint}
                        </p>
                      ) : null}
                    </div>
                    <ChevronDownIcon
                      className={cn(
                        "size-4 shrink-0 text-muted-foreground transition",
                        notesOpen && "rotate-180",
                      )}
                    />
                  </button>
                  {notesOpen ? (
                    <NotesBody
                      canEdit={canEdit}
                      complaint={complaint}
                      workNotes={workNotes}
                      jobComplaint={job.complaint}
                      jobWorkNotes={job.workNotes}
                      pending={updateMut.isPending}
                      onComplaint={setComplaint}
                      onWorkNotes={setWorkNotes}
                      onSave={() => void saveNotes()}
                    />
                  ) : null}
                </section>
              </div>

              {/* Right column — summaries + quote */}
              <div className="flex flex-col gap-4">
                <Section title="ອາໄຫຼ່" count={(job.parts ?? []).length}>
                  {shopId ? (
                    <WorkshopJobPartsSummary
                      shopId={shopId}
                      job={job}
                      canEdit={canEdit}
                      onEdit={openPartsEditor}
                    />
                  ) : null}
                </Section>

                <Section title="ຄ່າແຮງ" count={(job.labors ?? []).length}>
                  {shopId ? (
                    <WorkshopJobLaborsSummary
                      shopId={shopId}
                      job={job}
                      canEdit={canEdit}
                      onAdd={openAddLaborDialog}
                    />
                  ) : null}
                </Section>

                <Section title="ໃບສະເໜີ / ຊຳລະ">
                  {shopId ? (
                    <WorkshopQuoteCheckoutPanel
                      shopId={shopId}
                      job={job}
                      canEdit={canEdit}
                    />
                  ) : null}
                </Section>

                {/* Notes — mobile only (desktop has left column) */}
                <section className="rounded-2xl border border-border bg-card p-4 shadow-sm lg:hidden">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left touch-manipulation"
                    onClick={() => setNotesOpen((o) => !o)}
                  >
                    <div className="min-w-0">
                      <h2 className="font-semibold text-base">
                        ອາການ / ໝາຍເຫດ
                      </h2>
                      {!notesOpen && job.complaint ? (
                        <p className="mt-0.5 line-clamp-1 text-muted-foreground text-xs">
                          {job.complaint}
                        </p>
                      ) : null}
                    </div>
                    <ChevronDownIcon
                      className={cn(
                        "size-4 shrink-0 text-muted-foreground transition",
                        notesOpen && "rotate-180",
                      )}
                    />
                  </button>
                  {notesOpen ? (
                    <NotesBody
                      canEdit={canEdit}
                      complaint={complaint}
                      workNotes={workNotes}
                      jobComplaint={job.complaint}
                      jobWorkNotes={job.workNotes}
                      pending={updateMut.isPending}
                      onComplaint={setComplaint}
                      onWorkNotes={setWorkNotes}
                      onSave={() => void saveNotes()}
                    />
                  ) : null}
                </section>
              </div>
            </div>
          )}
        </div>

        {/* Sticky mobile CTA — above bottom nav */}
        {showStickyCta && primaryNext && job ? (
          <div
            className={cn(
              "fixed inset-x-0 z-30 border-border border-t bg-background/95 p-3 backdrop-blur",
              "bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:hidden",
            )}
          >
            <div className="mx-auto max-w-lg">
              <WorkshopStatusPrimaryButton
                to={primaryNext}
                from={job.status}
                pending={statusMut.isPending}
                onClick={() => handleStatusClick(primaryNext)}
              />
            </div>
          </div>
        ) : null}

        {shopId && job ? (
          <>
            <WorkshopPartsDialog
              open={partsDialogOpen}
              onOpenChange={setPartsDialogOpen}
              shopId={shopId}
              job={job}
              canEdit={canEdit}
            />
            <WorkshopLaborDialog
              open={laborDialogOpen}
              onOpenChange={setLaborDialogOpen}
              shopId={shopId}
              job={job}
            />
          </>
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}

function NotesBody({
  canEdit,
  complaint,
  workNotes,
  jobComplaint,
  jobWorkNotes,
  pending,
  onComplaint,
  onWorkNotes,
  onSave,
}: {
  canEdit: boolean;
  complaint: string;
  workNotes: string;
  jobComplaint: string;
  jobWorkNotes: string | null;
  pending: boolean;
  onComplaint: (v: string) => void;
  onWorkNotes: (v: string) => void;
  onSave: () => void;
}) {
  if (!canEdit) {
    return (
      <div className="mt-3 space-y-2 text-sm">
        <p>{jobComplaint || "—"}</p>
        {jobWorkNotes ? (
          <p className="text-muted-foreground">{jobWorkNotes}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="complaint">ອາການ</Label>
        <textarea
          id="complaint"
          rows={3}
          className={fieldClass}
          value={complaint}
          onChange={(e) => onComplaint(e.target.value)}
          placeholder="ລູກຄ້າບອກວ່າ…"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="notes">ໝາຍເຫດຊ່າງ</Label>
        <textarea
          id="notes"
          rows={2}
          className={fieldClass}
          value={workNotes}
          onChange={(e) => onWorkNotes(e.target.value)}
          placeholder="ບັນທຶກການກວດ / ສິ່ງທີ່ເຫັນ…"
        />
      </div>
      <Button
        type="button"
        variant="secondary"
        className="h-11 w-full touch-manipulation sm:w-auto"
        disabled={pending}
        onClick={onSave}
      >
        <SaveIcon className="size-4" />
        ບັນທຶກ
      </Button>
    </div>
  );
}
